import { useState, useRef, useEffect } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import {
  GalleryLightbox,
  GallerySharedStyles,
  MobileGalleryCarousel,
  type GalleryImage,
} from './_galleryShared';

/**
 * Unified photo gallery — a single masonry grid of every photo in the
 * property `photo_gallery` collection (with its lightbox), matching the
 * Homestead demo's gallery page: two columns of images at their natural
 * aspect ratios, packed with a tight gutter, sitting beneath the page
 * hero/intro blurb rather than being split into "Interior Luxuries" and
 * "Community Amenities" sub-sections.
 *
 * The page hero (HeroSolidBG, `blurbField: property_gallery_blurb`) supplies
 * the "Gallery" title and intro paragraph, so this section is intentionally
 * heading-less — it is just the grid.
 */
// Number of photos shown before the visitor opts in to see the rest. Anything
// beyond this is hidden until "Load More" expands the grid to the full set.
const INITIAL_VISIBLE = 15;

export function GalleryGrid() {
  const images = useCollectionBinding<GalleryImage>('gallerygrid.images', {
    collection: 'photo_gallery',
  });

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [expanded, setExpanded] = useState(false);

  // Collapsed: render the first INITIAL_VISIBLE photos. Expanded: render them
  // all. The toggle button only appears when there's more than one batch. The
  // lightbox is bound to the same visible slice so its indices and arrow
  // navigation stay consistent with what's on screen.
  const canToggle = images.length > INITIAL_VISIBLE;
  const visibleImages = expanded ? images : images.slice(0, INITIAL_VISIBLE);

  // When collapsed, clip the grid to the height of the SHORTER column so both
  // staggered columns end on one straight line (right around the last image)
  // rather than leaving an L-shaped white gap. The two columns use opposite
  // tall/short tile patterns, so their natural heights differ by ~one tile —
  // we measure them and clip to the min. Tiles carry an explicit aspectRatio,
  // so heights are deterministic from layout width even before images load;
  // the ResizeObserver re-measures on responsive width changes. On mobile the
  // masonry is display:none (height 0) → we skip clipping and let the carousel
  // size itself.
  const masonryRef = useRef<HTMLDivElement>(null);
  const [collapsedHeight, setCollapsedHeight] = useState<number | null>(null);

  const collapsed = canToggle && !expanded;

  useEffect(() => {
    if (!collapsed) {
      setCollapsedHeight(null);
      return;
    }
    const el = masonryRef.current;
    if (!el) return;
    const measure = () => {
      const cols = el.querySelectorAll<HTMLElement>('.gallery-grid__col');
      if (cols.length < 2) return;
      const heights = Array.from(cols, (c) => c.offsetHeight);
      const shorter = Math.min(...heights);
      // 0 means the masonry is hidden (mobile) — don't clip in that case.
      setCollapsedHeight(shorter > 0 ? shorter : null);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [collapsed, visibleImages.length]);

  // Split into two columns (even indices left, odd right). Reading order stays
  // interleaved (0 top-left, 1 top-right, 2, 3, …) and the lightbox keeps each
  // image's original index.
  const columns: { img: GalleryImage; index: number }[][] = [[], []];
  visibleImages.forEach((img, i) => columns[i % 2].push({ img, index: i }));

  if (images.length === 0) {
    return (
      <section className="bg-white">
        <div className="py-12 text-center text-[var(--vibe-color-text-muted)]">
          <p className="font-body text-lg">Gallery images will be displayed here.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-white">
      <div className="gallery-grid__container">
        <div
          className={`gallery-grid__viewport${collapsed ? ' is-collapsed' : ''}`}
          style={collapsed && collapsedHeight ? { maxHeight: collapsedHeight } : undefined}
        >
          {/* Tablet / desktop: staggered two-column masonry */}
          <div className="gallery-grid__masonry" ref={masonryRef}>
            {columns.map((col, ci) => (
            <div className="gallery-grid__col" key={ci}>
              {col.map(({ img, index }, pos) => {
                // Checkerboard of two tile shapes: short landscape tiles at
                // top-left / bottom-right, tall portrait tiles at top-right /
                // bottom-left. A tile is tall when (column + row) is odd, so
                // each column alternates and the two columns are offset —
                // producing the staggered look and re-aligning every two rows.
                const isTall = (ci + pos) % 2 === 1;
                return (
                  <button
                    key={index}
                    type="button"
                    className="gallery-grid__item gallery-item"
                    style={{ aspectRatio: isTall ? '5 / 6' : '5 / 3' }}
                    onClick={() => setSelectedIndex(index)}
                    aria-label={`Open image ${index + 1}`}
                  >
                    <VibesitesImage
                      url={img.image}
                      alt={img.alt_text || img.title || ''}
                      className="gallery-grid__img"
                    />
                  </button>
                );
              })}
            </div>
            ))}
          </div>

          {/* Mobile: single-image swipeable carousel that opens the lightbox on tap */}
          <div className="gallery-grid__mobile">
            <MobileGalleryCarousel
              images={visibleImages}
              onSelect={setSelectedIndex}
              showDots={false}
            />
          </div>

          {/* Collapsed: blurred fade over the bottom images with the Load More
              button floating on top, so the staggered grid edge reads as
              "there's more below" instead of an awkward gap. */}
          {canToggle && !expanded && (
            <div className="gallery-grid__fade">
              <button
                type="button"
                className="gallery-grid__more-btn"
                onClick={() => setExpanded(true)}
              >
                Load More
              </button>
            </div>
          )}
        </div>

        {/* Expanded: Load Less sits normally below the full grid */}
        {canToggle && expanded && (
          <div className="gallery-grid__more">
            <button
              type="button"
              className="gallery-grid__more-btn"
              onClick={() => setExpanded(false)}
            >
              Load Less
            </button>
          </div>
        )}
      </div>

      <GalleryLightbox
        images={visibleImages}
        selectedIndex={selectedIndex}
        onClose={() => setSelectedIndex(null)}
        onChange={setSelectedIndex}
      />

      {/* Lightbox styling (the row/carousel rules are unused here but harmless). */}
      <GallerySharedStyles />

      <style>{`
        .gallery-grid__container {
          max-width: 1080px;
          margin-left: auto;
          margin-right: auto;
          padding: 2.5rem 1.5rem 3.5rem;
        }

        /* Masonry: two columns, natural-ratio images, staggered heights.
           Desktop/tablet only — replaced by the swipeable carousel on mobile. */
        .gallery-grid__masonry {
          display: flex;
          align-items: flex-start;
          gap: 0.625rem;
        }

        /* Mobile carousel wrapper — hidden from tablet up. */
        .gallery-grid__mobile {
          display: none;
        }

        /* Anchor for the collapsed "fade + Load More" overlay. */
        .gallery-grid__viewport {
          position: relative;
        }

        /* Collapsed: clip both columns to one uniform height (set inline from the
           measured shorter column) so the staggered bottoms don't leave an
           L-shaped white gap. The fade then sits over a clean straight edge and
           the button reads as centered, not stranded. */
        .gallery-grid__viewport.is-collapsed {
          overflow: hidden;
        }

        /* Collapsed fade: a soft blur + gradient that dissolves the bottom rows
           into the page background, with the Load More button floating over it. */
        .gallery-grid__fade {
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          height: 300px;
          display: flex;
          align-items: flex-end;
          justify-content: center;
          padding-bottom: 2.25rem;
          pointer-events: none;
          background: linear-gradient(
            to bottom,
            rgba(255, 255, 255, 0) 0%,
            color-mix(in srgb, var(--vibe-color-background), transparent 35%) 55%,
            var(--vibe-color-background) 100%
          );
          -webkit-backdrop-filter: blur(3px);
          backdrop-filter: blur(3px);
          -webkit-mask-image: linear-gradient(to bottom, transparent 0%, #000 50%);
          mask-image: linear-gradient(to bottom, transparent 0%, #000 50%);
        }

        /* Only the button is interactive; the fade itself lets clicks fall to
           the images peeking out above it. */
        .gallery-grid__fade .gallery-grid__more-btn {
          pointer-events: auto;
        }

        .gallery-grid__col {
          flex: 1 1 0;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 0.625rem;
        }

        .gallery-grid__item {
          display: block;
          width: 100%;
          padding: 0;
          border: 0;
          background: none;
          overflow: hidden;
          cursor: pointer;
          position: relative;
          line-height: 0;
        }

        .gallery-grid__more {
          display: flex;
          justify-content: center;
          margin-top: 2.5rem;
        }

        /* Mirrors the header CTA (.header-cta--solid): secondary fill,
           on-secondary text, body font, 2px radius. */
        .gallery-grid__more-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.95rem 1.75rem;
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          font-weight: 600;
          letter-spacing: 0.03em;
          text-transform: none;
          border: none;
          border-radius: 2px;
          white-space: nowrap;
          cursor: pointer;
          background: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary);
          transition: background-color 0.25s ease, color 0.25s ease, opacity 0.25s ease;
        }

        .gallery-grid__more-btn:hover {
          background: color-mix(in srgb, var(--vibe-color-secondary), black 12%);
        }

        .gallery-grid__more-btn:focus-visible {
          outline: 2px solid var(--vibe-color-secondary);
          outline-offset: 2px;
        }

        .gallery-grid__img {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.6s ease;
        }

        .gallery-grid__item:hover .gallery-grid__img,
        .gallery-grid__item:focus-visible .gallery-grid__img {
          transform: scale(1.05);
        }

        .gallery-grid__item:focus-visible {
          outline: 2px solid var(--vibe-color-primary);
          outline-offset: 2px;
        }

        @media (prefers-reduced-motion: reduce) {
          .gallery-grid__img { transition: none; }
        }

        @media (max-width: 639px) {
          .gallery-grid__container {
            padding: 1.5rem 0.75rem 2.5rem;
          }
          /* Swap the masonry grid for the single-image swipeable carousel */
          .gallery-grid__masonry {
            display: none;
          }
          /* No grid to clip on mobile — let the carousel size itself. */
          .gallery-grid__viewport.is-collapsed {
            max-height: none;
            overflow: visible;
          }
          .gallery-grid__mobile {
            display: block;
          }
          /* The carousel shows one image at a time, so the blur-over-grid effect
             doesn't apply — drop the fade back to a normal centered button. */
          .gallery-grid__fade {
            position: static;
            height: auto;
            padding-bottom: 0;
            margin-top: 1.75rem;
            background: none;
            -webkit-backdrop-filter: none;
            backdrop-filter: none;
            -webkit-mask-image: none;
            mask-image: none;
            pointer-events: auto;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'GalleryGrid',
  displayName: 'Photo Gallery',
  tokens: {},
  collectionBindings: {
    'gallerygrid.images': {
      label: 'Gallery Photos',
      collection: 'photo_gallery',
      description:
        'Every photo from the property photo gallery, shown together in one grid.',
    },
  },
};

(GalleryGrid as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default GalleryGrid;
